import React, { useState, useEffect, useRef } from 'react';
import { walletAmount, BbIcon, RobuxIcon, WalletValue, useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Spade, History, Loader2, ArrowDownToLine, Scissors, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useChatLayout } from '../context/ChatLayoutContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// ==========================================
// K, M, B FORMATLAYICILARI (ÇİFT YÖNLÜ)
// ==========================================
const parseAmount = (valStr) => {
  if (!valStr) return 0;
  // ⚠️ BİNLİK AYIRICI TEMİZLENMELİ. formatAmount() büyük sayıları
  // toLocaleString ile üretiyor ("1,457,545"); parseFloat virgülde durup
  // 1 döndürüyordu. MAX butonuna basan kullanıcı 1 birim bahis oynuyordu.
  let str = valStr.toString().trim().toLowerCase().replace(/[,\s]/g, '');
  let multiplier = 1;
  if (str.endsWith('k')) { multiplier = 1000; str = str.slice(0, -1); }
  else if (str.endsWith('m')) { multiplier = 1000000; str = str.slice(0, -1); }
  else if (str.endsWith('b')) { multiplier = 1000000000; str = str.slice(0, -1); }
  
  const parsedValue = parseFloat(str);
  return isNaN(parsedValue) ? 0 : Math.floor(parsedValue * multiplier);
};

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === 0) return '';
  if (amount >= 1000000) return amount.toLocaleString();
  if (amount >= 1000) return parseFloat((amount / 1000).toFixed(2)) + 'K';
  return amount.toString();
};

// ==========================================
// KART BİLEŞENİ
// ==========================================
const PlayingCard = ({ card, hidden, index, isIdle, compact = false }) => {
  const widthClass = compact ? "w-12 md:w-20" : "w-16 md:w-24";
  const heightClass = compact ? "h-16 md:h-28" : "h-24 md:h-36";
  const marginClass = compact ? "-ml-4 md:-ml-8" : "-ml-6 md:-ml-10";

  if (hidden) {
    return (
      <motion.div 
        initial={isIdle ? { opacity: 0 } : { opacity: 0, x: 50, y: -50, rotate: 10 }}
        animate={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
        transition={{ delay: isIdle ? 0 : index * 0.15, type: 'spring', stiffness: 100 }}
        className={`${widthClass} ${heightClass} rounded-lg md:rounded-xl shadow-xl border-[3px] border-zinc-300 relative overflow-hidden ${marginClass} first:ml-0 flex-shrink-0 bg-white ${isIdle ? 'opacity-40 grayscale' : ''}`}
      >
        <div className="absolute inset-1 bg-[#15171a] rounded border border-[#16A34A]/30 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url('/logo.png')" }}></div>
            <img src="/logo.png" alt="Card Back" className="w-8 h-8 md:w-12 md:h-12 z-10 drop-shadow-[0_0_8px_rgba(22,163,74,0.8)]" />
        </div>
      </motion.div>
    );
  }

  const isRed = card.suit === 'Hearts' || card.suit === 'Diamonds';
  const getSuitSymbol = (suit) => {
    switch(suit) {
      case 'Hearts': return '♥';
      case 'Diamonds': return '♦';
      case 'Clubs': return '♣';
      case 'Spades': return '♠';
      default: return '';
    }
  };

  return (
    <motion.div 
        initial={{ opacity: 0, x: 50, y: -50, rotate: 10 }}
        animate={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
        transition={{ delay: index * 0.15, type: 'spring', stiffness: 100 }}
        className={`${widthClass} ${heightClass} rounded-lg md:rounded-xl shadow-[2px_4px_12px_rgba(0,0,0,0.5)] border-[3px] border-zinc-200 bg-white relative flex flex-col justify-between p-1 md:p-2 ${marginClass} first:ml-0 flex-shrink-0`}
    >
      <div className={`flex flex-col items-center leading-none ${isRed ? 'text-red-600' : 'text-slate-900'}`}>
        <span className={`${compact ? 'text-xs md:text-lg' : 'text-sm md:text-xl'} font-bold font-heading`}>{card.value}</span>
        <span className={`${compact ? 'text-xs md:text-lg' : 'text-sm md:text-xl'}`}>{getSuitSymbol(card.suit)}</span>
      </div>
      
      <div className={`absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none ${compact ? 'text-3xl md:text-5xl' : 'text-4xl md:text-6xl'} ${isRed ? 'text-red-600' : 'text-slate-900'}`}>
        {getSuitSymbol(card.suit)}
      </div>

      <div className={`flex flex-col items-center leading-none rotate-180 self-end ${isRed ? 'text-red-600' : 'text-slate-900'}`}>
        <span className={`${compact ? 'text-xs md:text-lg' : 'text-sm md:text-xl'} font-bold font-heading`}>{card.value}</span>
        <span className={`${compact ? 'text-xs md:text-lg' : 'text-sm md:text-xl'}`}>{getSuitSymbol(card.suit)}</span>
      </div>
    </motion.div>
  );
};

export const Blackjack = () => {
  const { user, token, isAuthenticated, balance, fmtMoney, wallet, betToRaw, rawToBet, betLimits } = useAuth();
  const [betAmount, setBetAmount] = useState('');
  
  const [gameState, setGameState] = useState('idle'); 
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [hands, setHands] = useState([]);
  const [activeHandIndex, setActiveHandIndex] = useState(0); 

  const [dealerCards, setDealerCards] = useState([]);
  const [dealerTotal, setDealerTotal] = useState(0);
  
  const [serverSeedHash, setServerSeedHash] = useState(null);
  const [serverSeed, setServerSeed] = useState(null);
  const [gameResult, setGameResult] = useState(null); 
  const [winAmount, setWinAmount] = useState(0);
  const [resultMessage, setResultMessage] = useState('');

  const [history, setHistory] = useState([]);
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();

  const drawSound = useRef(null);
  const winSound = useRef(null);
  const loseSound = useRef(null);

  useEffect(() => {
    if (typeof Audio !== "undefined") {
      drawSound.current = new Audio('/cardSound.mp3');
      drawSound.current.preload = 'auto';
      winSound.current = new Audio('/towerWin.wav');
      winSound.current.preload = 'auto';
      loseSound.current = new Audio('/explosion.mp3');
      loseSound.current.preload = 'auto';
    }
  }, []);

  const calculateVisibleTotal = (cards) => {
      if (!cards || cards.length === 0) return 0;
      let total = 0;
      let aces = 0;
      cards.forEach(c => {
          if (c.value === 'A') { total += 11; aces += 1; }
          else if (['J', 'Q', 'K'].includes(c.value)) total += 10;
          else total += parseInt(c.value);
      });
      while (total > 21 && aces > 0) { total -= 10; aces -= 1; }
      return total;
  };

  useEffect(() => {
    if (gameState === 'playing' && hands.length > 0 && !isProcessing) {
        const currentHand = hands[activeHandIndex];
        if (currentHand && currentHand.status === 'playing') {
            const currentTotal = calculateVisibleTotal(currentHand.cards);
            if (currentTotal === 21) {
                const timer = setTimeout(() => {
                    handleAction('stand');
                }, 1000);
                return () => clearTimeout(timer);
            }
        }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hands, activeHandIndex, gameState, isProcessing]);

  useEffect(() => {
    const checkActiveGame = async () => {
      if (!isAuthenticated || !token) return;
      try {
        const response = await axios.get(`${API}/game/blackjack/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data;
        
        if (data.has_active_game) {
          setGameState('playing');
          if (data.hands) {
              setHands(data.hands);
              const nextActive = data.hands.findIndex(h => h.status === 'playing');
              setActiveHandIndex(nextActive !== -1 ? nextActive : 0);
          } else if (data.player_cards) {
              setHands([{ cards: data.player_cards, bet: data.bet, status: 'playing' }]);
              setActiveHandIndex(0);
          }
          
          setDealerCards(data.dealer_cards);
          setBetAmount(String(rawToBet(data.bet)));
          toast.info("Resuming your active game!");
        }
      } catch (error) {
        console.error("Active game check failed:", error);
      }
    };
    
    checkActiveGame();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  const playDrawSound = () => {
      if (drawSound.current) {
          const soundClone = drawSound.current.cloneNode();
          soundClone.volume = 0.4;
          soundClone.play().catch(() => {});
      }
  };

  const playOutcomeSound = (win) => {
      if (win && winSound.current) {
          const soundClone = winSound.current.cloneNode();
          soundClone.volume = 0.5;
          soundClone.play().catch(() => {});
      } else if (!win && loseSound.current) {
          const soundClone = loseSound.current.cloneNode();
          soundClone.volume = 0.6;
          soundClone.play().catch(() => {});
      }
  };

  const handleBetChange = (val) => setBetAmount(val);
  const addBetAmount = (val) => {
    const current = parseAmount(betAmount);
    setBetAmount(String(current + val));
  };
  const multiplyBet = (mult) => {
    const current = parseAmount(betAmount);
    setBetAmount(String(Math.floor(current * mult)));
  };

  const startGame = async () => {
    // 🎯 Kullanıcı AKTİF CÜZDANIN biriminde yazar (Robux → site birimi,
    // BB Coin → BB). betToRaw saklama birimine çevirir: BB'de ×1000.
    // Bu satır olmadan "10" yazan oyuncu 10 mili-BB (0,01 BB) oynardı.
    const amount = betToRaw(parseAmount(betAmount));
    
    if (!isAuthenticated) return toast.error("Please log in first!");
    // ⚠️ Limitler cüzdana göre; "1 – 100.000" SİTE birimidir ve
    // BB Coin bahislerini haksız yere reddederdi.
    if (!amount || amount < betLimits.min_bet || amount > betLimits.max_bet) {
      return toast.error(`Bet must be between ${walletAmount(betLimits.min_bet, wallet)} and ${walletAmount(betLimits.max_bet, wallet)}.`);
    }
    // 🍀 AKTİF CÜZDANIN bakiyesiyle karşılaştır (LC'de mili-LC).
    if (amount > balance) return toast.error("Insufficient balance!");

    setIsProcessing(true);
    setGameResult(null);
    setResultMessage('');
    setServerSeed(null);
    setDealerTotal(0);

    try {
      const response = await axios.post(`${API}/game/blackjack/start`, { amount }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      const data = response.data;

      playDrawSound();
      
      if (data.hands) {
          setHands(data.hands);
      } else {
          setHands([{ cards: data.player_cards, bet: amount, status: 'playing' }]);
      }
      
      setActiveHandIndex(0);
      setDealerCards(data.dealer_cards);
      setGameState(data.status);

      if (data.status === 'finished') {
          setTimeout(() => {
              setDealerTotal(data.dealer_total);
              setServerSeed(data.server_seed);
              setWinAmount(data.win_amount);
              
              let resultStr = 'lose';
              if (data.win_amount > amount) resultStr = 'win';
              else if (data.win_amount === amount) resultStr = 'push';
              
              setGameResult(resultStr);
              setResultMessage(data.message || (resultStr === 'win' ? 'BLACKJACK!' : (resultStr === 'push' ? 'PUSH' : 'DEALER WINS')));
              playOutcomeSound(resultStr === 'win' || resultStr === 'push'); 
              addToHistory(resultStr === 'win' ? 2.5 : (resultStr === 'push' ? 1 : 0));
              setIsProcessing(false); 
          }, 800);
      } else {
          setServerSeedHash(data.server_seed_hash);
          setIsProcessing(false); 
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred!");
      setIsProcessing(false); 
    }
  };

  const handleAction = async (action) => {
      if (gameState !== 'playing' || isProcessing) return;
      setIsProcessing(true);

      try {
          const response = await axios.post(`${API}/game/blackjack/action`, { action, hand_index: activeHandIndex }, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
          const data = response.data;

          if (['hit', 'double', 'split'].includes(action)) {
              playDrawSound();
          }

          if (data.hands) setHands(data.hands);

          if (data.status === 'finished') {
              setGameState('finished'); 
              const finalDealerCards = data.dealer_cards;

              setTimeout(() => {
                  setDealerCards(finalDealerCards.slice(0, 2));
                  
                  const pTotal = data.player_total || calculateVisibleTotal(data.hands?.[0]?.cards || hands[0].cards);
                  
                  if (pTotal <= 21 && finalDealerCards.length >= 2) {
                      playDrawSound();
                  }

                  let step = 2;
                  const dealNextCard = () => {
                      if (step < finalDealerCards.length) {
                          setDealerCards(finalDealerCards.slice(0, step + 1));
                          playDrawSound();
                          step++;
                          setTimeout(dealNextCard, 800);
                      } else {
                          setDealerTotal(data.dealer_total);
                          setServerSeed(data.server_seed);
                          setWinAmount(data.win_amount);
                          
                          const totalBet = (data.hands || hands).reduce((acc, h) => acc + h.bet, 0);
                          
                          let isWin = false;
                          let resMsg = 'DEALER WINS';
                          let resStr = 'lose';
                          
                          if (data.win_amount > totalBet) {
                              isWin = true;
                              resMsg = 'YOU WIN!';
                              resStr = 'win';
                          } else if (data.win_amount === totalBet) {
                              isWin = true; 
                              resMsg = 'PUSH (TIE)';
                              resStr = 'push';
                          } else if (data.win_amount > 0) {
                              isWin = true;
                              resMsg = 'MIXED RESULT';
                              resStr = 'mixed';
                          } else {
                              const allBusted = (data.hands || hands).every(h => calculateVisibleTotal(h.cards) > 21);
                              resMsg = allBusted ? 'BUST! YOU LOSE.' : 'DEALER WINS';
                              resStr = 'lose';
                          }
                          
                          setGameResult(resStr);
                          setResultMessage(data.message || resMsg);
                          
                          playOutcomeSound(isWin);
                          addToHistory(data.win_amount > 0 ? parseFloat((data.win_amount / totalBet).toFixed(2)) : 0);
                          setIsProcessing(false); 
                      }
                  };

                  if (finalDealerCards.length > 2) {
                      setTimeout(dealNextCard, 800);
                  } else {
                      setTimeout(dealNextCard, 500); 
                  }
              }, 500); 

          } else {
              setGameState(data.status);
              const nextActive = (data.hands || hands).findIndex(h => h.status === 'playing');
              if (nextActive !== -1) setActiveHandIndex(nextActive);
              
              setIsProcessing(false); 
          }
      } catch (error) {
          toast.error(error.response?.data?.detail || "An error occurred!");
          setIsProcessing(false); 
      }
  };

  const addToHistory = (mult) => {
    setHistory(prev => [{ id: Date.now(), mult, win: mult > 0 }, ...prev].slice(0, 10));
  };

  const displayDealerTotal = calculateVisibleTotal(dealerCards);
  
  const activeHand = hands[activeHandIndex];
  const canDouble = activeHand && activeHand.cards.length === 2 && activeHand.status === 'playing';
  const canSplit = hands.length === 1 && activeHand && activeHand.cards.length === 2 && activeHand.status === 'playing' && 
                   activeHand.cards[0].numeric === activeHand.cards[1].numeric;


  return (
    <div className="min-h-screen bg-[#0E0F11]" data-testid="blackjack-page">
      <div className="max-w-[1800px] mx-auto px-4 pt-24 pb-8">
        <div className={`flex flex-col lg:flex-row gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
          <main className="flex-1 flex flex-col lg:flex-row gap-6 min-w-0 lg:h-[calc(100vh-120px)]">
            
            {/* SOL SÜTUN: KONTROLLER */}
            <div className="w-full lg:w-80 flex-shrink-0 flex flex-col gap-4 h-full">
              <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-4 shadow-lg h-full flex flex-col">
                <div className="space-y-6 flex-1">
                  
                  <div className={`transition-opacity duration-300 ${(gameState === 'playing') ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                    <label className="text-xs text-zinc-400 font-bold mb-1.5 block uppercase tracking-wider">Bet Amount</label>
                    <div className="relative mb-3">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        {/* Birim öneki AKTİF cüzdana göre; sabit "$" BB tarafında yanlıştı. */}
                        {wallet === 'lc' ? <BbIcon size={15} /> : <RobuxIcon size={15} />}
                      </div>
                      <input
                        type="text"
                        value={betAmount}
                        onChange={(e) => handleBetChange(e.target.value)}
                        className="w-full bg-[#15171a] border border-[#2A2D31] rounded-xl py-3 pl-10 pr-4 text-white font-bold outline-none focus:border-[#B059E8] transition-colors uppercase"
                        placeholder="1"
                        disabled={gameState === 'playing'}
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <button onClick={() => addBetAmount(10)} disabled={gameState === 'playing'} className="py-2 text-xs font-bold text-zinc-300 bg-[#2A2D31]/50 hover:bg-[#33363b] rounded-lg transition-colors">+10</button>
                      <button onClick={() => multiplyBet(0.5)} disabled={gameState === 'playing'} className="py-2 text-xs font-bold text-zinc-300 bg-[#2A2D31]/50 hover:bg-[#33363b] rounded-lg transition-colors">1/2</button>
                      <button onClick={() => multiplyBet(2)} disabled={gameState === 'playing'} className="py-2 text-xs font-bold text-zinc-300 bg-[#2A2D31]/50 hover:bg-[#33363b] rounded-lg transition-colors">2x</button>
                      <button onClick={() => setBetAmount(String(rawToBet(balance || 0)))} disabled={gameState === 'playing'} className="py-2 text-xs font-bold text-[#2ecf6e] bg-[#16A34A]/20 border border-[#16A34A]/30 hover:bg-[#16A34A]/40 rounded-lg transition-colors">MAX</button>
                    </div>
                  </div>

                  <div className="bg-[#15171a] border border-[#2A2D31] rounded-xl p-4">
                     <span className="text-zinc-500 text-xs font-bold uppercase tracking-wider block mb-1">Potential Win (2x)</span>
                     <span className="text-[#2ecf6e] mm-num font-bold text-xl">
                        <WalletValue amount={betAmount ? betToRaw(parseAmount(betAmount)) * 2 : 0} wallet={wallet} />
                     </span>
                  </div>

                </div>

                <div className="mt-auto pt-6 flex flex-col gap-3">
                    {gameState === 'playing' ? (
                        <div className="flex flex-col gap-3">
                            <div className="grid grid-cols-2 gap-3">
                                <button 
                                    onClick={() => handleAction('hit')}
                                    disabled={isProcessing}
                                    className="py-4 rounded-xl font-heading font-bold text-lg uppercase tracking-wider transition-colors duration-200 shadow-md bg-[#16A34A] hover:bg-[#15913f] text-white disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isProcessing && <Loader2 className="w-5 h-5 animate-spin" />} HIT
                                </button>
                                <button 
                                    onClick={() => handleAction('stand')}
                                    disabled={isProcessing}
                                    className="py-4 rounded-xl font-heading font-bold text-lg uppercase tracking-wider transition-colors duration-200 shadow-md bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    STAND
                                </button>
                            </div>
                            
                            {(canDouble || canSplit) && (
                                <div className="grid grid-cols-2 gap-3">
                                    <button 
                                        onClick={() => handleAction('double')}
                                        disabled={isProcessing || !canDouble}
                                        className={`py-3 rounded-xl font-heading font-bold text-sm uppercase tracking-wider transition-colors duration-200 shadow-md flex items-center justify-center gap-2
                                            ${canDouble ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' : 'bg-[#2A2D31]/50 text-zinc-500 border border-[#2A2D31]'}`}
                                    >
                                        <ArrowDownToLine className="w-4 h-4" /> 2X DOUBLE
                                    </button>
                                    <button 
                                        onClick={() => handleAction('split')}
                                        disabled={isProcessing || !canSplit}
                                        className={`py-3 rounded-xl font-heading font-bold text-sm uppercase tracking-wider transition-colors duration-200 shadow-md flex items-center justify-center gap-2
                                            ${canSplit ? 'bg-[#B059E8] hover:bg-[#7b2fc4] text-white' : 'bg-[#2A2D31]/50 text-zinc-500 border border-[#2A2D31]'}`}
                                    >
                                        <Scissors className="w-4 h-4" /> SPLIT
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <button 
                            onClick={startGame}
                            disabled={isProcessing || !betAmount}
                            className="w-full py-4 rounded-xl font-heading font-bold text-lg uppercase tracking-wider transition-colors duration-200 shadow-md bg-[#16A34A] hover:bg-[#15913f] text-white disabled:opacity-50 disabled:grayscale flex items-center justify-center gap-2"
                        >
                            {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : ''} 
                            {gameState === 'finished' ? 'DEAL AGAIN' : 'DEAL CARDS'}
                        </button>
                    )}
                </div>
              </div>
            </div>

            {/* SAĞ SÜTUN: OYUN TAHTASI */}
            <div className="flex-1 flex flex-col items-center bg-gradient-to-b from-[#1A1D20] to-[#15171a] rounded-2xl border border-[#2A2D31] shadow-2xl relative overflow-hidden h-full p-4 lg:p-8">
              
              <div className="absolute top-4 left-4 md:top-6 md:left-6 flex flex-col z-10">
                <div className="flex items-center gap-2 mb-2">
                    <Spade className="w-6 h-6 text-zinc-300" />
                    <span className="text-zinc-400 font-bold uppercase tracking-widest text-sm">Blackjack</span>
                </div>
              </div>

              <div className="absolute top-4 right-4 md:top-6 md:right-6 flex flex-col items-center z-30 pointer-events-none">
                <span className="text-zinc-500 font-bold flex items-center gap-1 uppercase tracking-widest text-[9px] md:text-[10px] mb-2">
                  <History className="w-3 h-3" /> Recent
                </span>
                
                <div className="flex flex-col gap-1.5 overflow-hidden max-h-[250px] md:max-h-[300px] [mask-image:linear-gradient(to_bottom,white_70%,transparent_100%)] p-1">
                  <AnimatePresence>
                    {history.map((item) => (
                       <motion.div
                          layout 
                          key={item.id}
                          initial={{ opacity: 0, y: -20, scale: 0.8 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.15 } }}
                          className={`w-12 h-8 rounded-md flex-shrink-0 flex items-center justify-center font-bold text-xs shadow-lg border-2 
                             ${item.win ? 'bg-[#16A34A]/20 text-[#2ecf6e] border-[#16A34A]/50' : 'bg-[#15171a] text-zinc-500 border-[#2A2D31]'}`}
                       >
                          {item.mult}x
                       </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>

              {/* ========================================== */}
              {/* OYUN MASASI (ÇUHA) */}
              {/* ========================================== */}
              {/* Kartları ve dealerı topluca yukarı kaydırmak için justify-center ve pb-32 eklendi */}
              <div className="flex-1 flex flex-col items-center justify-center w-full max-w-4xl pb-24 md:pb-32 pt-4 relative gap-16 md:gap-24">
                 
                 <AnimatePresence>
                    {gameState === 'finished' && resultMessage && (
                        <motion.div 
                            initial={{ scale: 0.5, opacity: 0, y: '-50%', x: '-50%' }}
                            animate={{ scale: 1, opacity: 1, y: '-50%', x: '-50%' }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            className="absolute top-1/2 left-1/2 z-50 pointer-events-none flex flex-col items-center"
                        >
                            <div className={`px-8 py-3 rounded-2xl font-heading font-black text-3xl md:text-5xl shadow-2xl border-4 whitespace-nowrap uppercase tracking-widest
                                ${gameResult === 'win' ? 'bg-[#16A34A] text-white border-white shadow-[0_0_40px_rgba(22,163,74,0.8)]' 
                                : gameResult === 'push' || gameResult === 'mixed' ? 'bg-amber-500 text-slate-900 border-white shadow-[0_0_40px_rgba(245,158,11,0.8)]'
                                : 'bg-rose-600 text-white border-white shadow-[0_0_40px_rgba(225,29,72,0.8)]'}
                            `}>
                                {resultMessage}
                            </div>
                            {(gameResult === 'win' || gameResult === 'push' || gameResult === 'mixed') && winAmount > 0 && (
                                <div className={`mt-4 bg-[#15171a] border px-6 py-2 rounded-full font-bold mm-num text-xl shadow-lg
                                   ${gameResult === 'push' ? 'border-amber-500 text-amber-400' : 'border-[#16A34A] text-[#2ecf6e]'}`}>
                                    +<WalletValue amount={winAmount} wallet={wallet} />
                                </div>
                            )}
                        </motion.div>
                    )}
                 </AnimatePresence>

                 {/* 21 FİLİGRANI (Artık araya girip elementleri itmiyor, absolute olarak arkada duruyor) */}
                 <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] z-0">
                    <span className="font-heading font-black text-8xl md:text-[12rem] tracking-[0.5em] ml-[0.5em]">21</span>
                 </div>

                 {/* KURPİYERİN ALANI */}
                 <div className="flex flex-col items-center w-full z-10">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="text-zinc-500 font-bold uppercase tracking-widest text-sm">Dealer</span>
                        {displayDealerTotal > 0 && (
                            <motion.span 
                                key={displayDealerTotal}
                                initial={{ scale: 1.2, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className={`px-3 py-1.5 md:px-4 rounded-xl border-2 text-lg md:text-xl font-black shadow-md text-white transition-colors duration-300
                                    ${displayDealerTotal > 21 ? 'bg-rose-600/90 border-rose-400' 
                                    : displayDealerTotal === 21 ? 'bg-[#16A34A] border-[#2ecf6e]' 
                                    : 'bg-[#2A2D31] border-[#3a3d43]'}`}
                            >
                                {displayDealerTotal}
                            </motion.span>
                        )}
                    </div>
                    
                    <div className="flex items-center justify-center ml-6 md:ml-10">
                        {gameState === 'idle' ? (
                            <>
                                <PlayingCard hidden={true} index={0} isIdle={true} />
                                <PlayingCard hidden={true} index={1} isIdle={true} />
                            </>
                        ) : (
                            <>
                                {dealerCards.map((card, idx) => (
                                    <PlayingCard key={`dealer-${idx}`} card={card} index={idx} />
                                ))}
                                {gameState === 'playing' && dealerCards.length === 1 && (
                                    <PlayingCard hidden={true} index={1} />
                                )}
                            </>
                        )}
                    </div>
                 </div>

                 {/* OYUNCUNUN ALANI (Çoklu El Döngüsü) */}
                 <div className="flex justify-center items-end w-full relative gap-8 md:gap-16 z-10">
                    {gameState === 'idle' ? (
                         <div className="flex flex-col items-center">
                             <div className="flex items-center justify-center ml-6 md:ml-10 mb-4">
                                 <PlayingCard hidden={true} index={0} isIdle={true} />
                                 <PlayingCard hidden={true} index={1} isIdle={true} />
                             </div>
                             <div className="flex items-center gap-4 mt-6">
                                 <span className="text-zinc-500 font-bold uppercase tracking-widest text-sm">You</span>
                             </div>
                         </div>
                    ) : (
                        hands.map((hand, hIdx) => {
                            const handTotal = calculateVisibleTotal(hand.cards);
                            const isCompact = hands.length > 1; 
                            const isCurrentActive = hIdx === activeHandIndex && gameState === 'playing';

                            return (
                                <div key={`hand-${hIdx}`} className={`flex flex-col items-center relative transition-all duration-300 ${!isCurrentActive && gameState === 'playing' ? 'opacity-50 scale-90' : 'scale-100'}`}>
                                    
                                    {isCurrentActive && (
                                        <motion.div 
                                            layoutId="activeHandIndicator"
                                            className="absolute -top-8 text-[#16A34A] font-bold flex items-center gap-1"
                                        >
                                            <ArrowDownToLine className="w-4 h-4 animate-bounce" /> Playing
                                        </motion.div>
                                    )}

                                    <div className={`flex items-center justify-center ${isCompact ? 'ml-4 md:ml-8' : 'ml-6 md:ml-10'}`}>
                                        <AnimatePresence>
                                            {hand.cards.map((card, idx) => (
                                                <PlayingCard key={`player-${hIdx}-${idx}`} card={card} index={idx} compact={isCompact} />
                                            ))}
                                        </AnimatePresence>
                                    </div>

                                    <div className="flex items-center gap-2 mt-6 whitespace-nowrap">
                                        <span className="text-zinc-500 font-bold uppercase tracking-widest text-xs md:text-sm">
                                            {hands.length > 1 ? `Hand ${hIdx + 1}` : 'You'}
                                        </span>
                                        {handTotal > 0 && (
                                            <motion.span 
                                                key={`total-${hIdx}-${handTotal}`}
                                                initial={{ scale: 1.2, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1 }}
                                                className={`px-3 py-1.5 rounded-xl border-2 text-sm md:text-lg font-black shadow-md text-white transition-colors duration-300
                                                    ${handTotal > 21 ? 'bg-rose-600/90 border-rose-400' 
                                                    : handTotal === 21 ? 'bg-[#16A34A] border-[#2ecf6e]' 
                                                    : 'bg-[#2A2D31] border-[#3a3d43]'}`}
                                            >
                                                {handTotal}
                                            </motion.span>
                                        )}
                                        {hands.length > 1 && (
                                            <span className="text-[#2ecf6e] mm-num text-xs bg-[#15171a] px-2 py-1 rounded border border-[#2A2D31]">
                                                <WalletValue amount={hand.bet} wallet={wallet} />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                 </div>

              </div>

              {/* 🛡️ PROVABLY FAIR VE BİLGİLENDİRME ALANI */}
              <div className="absolute bottom-4 left-4 right-4 flex flex-col md:flex-row items-center justify-between gap-2 text-[10px] md:text-xs text-zinc-500 mm-num pointer-events-none px-2 mt-4">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-[#16A34A]" />
                  <span>Provably Fair</span>
                  <span className="ml-2 opacity-50 hidden md:inline">| Infinite Deck Shuffling</span>
                </div>
                
                <div className="flex gap-4 w-full md:w-auto overflow-hidden opacity-70">
                    {(gameState === 'finished' && serverSeed) ? (
                        <span className="truncate text-[#16A34A]" title={serverSeed}>
                            <span className="text-[#15913f]">Seed:</span> {serverSeed.substring(0, 32)}...
                        </span>
                    ) : serverSeedHash ? (
                        <span className="truncate text-zinc-400" title={serverSeedHash}>
                            <span className="text-zinc-600">Hash:</span> {serverSeedHash.substring(0, 32)}...
                        </span>
                    ) : null}
                </div>
              </div>

            </div>
          </main>
        </div>
      </div>
    </div>
  );
};